import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../auth/AuthContext";
import { ShieldCheck } from "lucide-react";
import { formatNumber } from "../lib/currencyDisplay";

/** Prefer live policy API; fall back to booking.insurance snapshot when present. */
export default function PolicyCard({ bookingId, insurance: insuranceProp }) {
  const [policy, setPolicy] = useState(insuranceProp || null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (insuranceProp?.policy_number || insuranceProp?.provider || insuranceProp?.included) {
      setPolicy(insuranceProp);
    }
  }, [insuranceProp]);

  useEffect(() => {
    if (!bookingId) return;
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API}/policies/booking/${bookingId}`, { withCredentials: true });
        if (alive) setPolicy(r.data);
      } catch (e) {
        if (alive && !insuranceProp) setErr(true);
      }
    })();
    return () => { alive = false; };
  }, [bookingId, insuranceProp]);

  if (err || !policy) return null;

  const isBc = policy.region === "BC" || policy.program === "bc_icbc_territory"
    || String(policy.provider || "").includes("Z-Cover BC");
  const coveragePath = policy.coverage_details_path || "/coverage";

  return (
    <div className="zc-card p-5" data-testid="policy-card" data-region={policy.region || "default"}>
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-full grid place-items-center bg-[var(--zc-accent-soft)] text-[var(--zc-accent)]">
          <ShieldCheck size={18} />
        </span>
        <div className="flex-1 min-w-0">
          <div className="eyebrow">Trip protection</div>
          <div className="zc-title text-lg leading-tight mt-0.5">
            Insurance coverage included with your rental
          </div>
        </div>
        <span className="zc-badge text-[var(--zc-success)] font-semibold">{policy.status || "included"}</span>
      </div>

      {(policy.policy_number || policy.coverage_amount != null || policy.deductible != null) && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-4 pt-4 border-t border-[var(--zc-border)]">
          {policy.policy_number && (
            <div>
              <div className="eyebrow">Policy #</div>
              <div className="font-mono text-xs mt-1" data-testid="policy-number">{policy.policy_number}</div>
            </div>
          )}
          {policy.coverage_amount != null && (
            <div>
              <div className="eyebrow">Coverage limit</div>
              <div className="font-semibold text-sm mt-1">${Number(policy.coverage_amount).toLocaleString()}</div>
            </div>
          )}
          {policy.deductible != null && (
            <div>
              <div className="eyebrow">Deductible</div>
              <div className="font-semibold text-sm mt-1">${formatNumber(policy.deductible, 0)}</div>
            </div>
          )}
        </div>
      )}

      <div className="zc-hint mt-3">
        {isBc
          ? "British Columbia trips include Z-Cover BC protection for the rental period."
          : "Per-trip protection is included with your rental."}
        {" "}
        <Link to={coveragePath} className="text-[var(--zc-accent)] hover:underline" data-testid="policy-coverage-link">
          Coverage details
        </Link>
      </div>
    </div>
  );
}
